import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays, compareAsc } from "date-fns";
import { 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import { fetchJumpSeaters } from 'state/api';
import { filterFlights } from 'state/helpers';
import { countFlightsByField } from './utils';
import Header from 'components/Header';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "jumpseatersByDate", label: "by Date" },
  { value: "jumpseatersByDep", label: "by Port" },
  { value: "jumpseatersBySector", label: "by Sector" },
  { value: "jumpseatersByFlightReg", label: "by Flight Reg" },
  { value: "jumpseatersByACType", label: "by AC Type" },
];

const NonCrewListChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    
    const [view, setView] = useState('jumpseatersByDate');
    const [jumpSeaters, setJumpSeaters] = useState([]);
    const [data, setData] = useState({
        jumpseatersByDate: [],        
        jumpseatersByDep: [],
        jumpseatersBySector: [],
        jumpseatersByFlightReg: [],
        jumpseatersByACType: [],
    });

    useEffect(() => {    
        const handleJumpSeaters = async () => {
            try {
                setLoading(true);
                const result = await fetchJumpSeaters(addDays(startDate, -1), addDays(endDate, 1));
                
                if (result) {        
                const filteredSeaters = result.filter((seater) => {            
                    const flightDateToCompare =
                    timeMode === 'UTC' ? seater.flightDate : seater.flightDateLocalBase;
                
                    return (
                    compareAsc(flightDateToCompare, startDate) >= 0 &&
                    compareAsc(flightDateToCompare, endDate) <= 0 && 
                    seater.seaterType !== 'crew'
                    );
                });
                
                setJumpSeaters(filteredSeaters);  
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };
    
        handleJumpSeaters();      
    }, [timeMode, startDate, endDate]);  
    
    const filteredJumpSeaters = useMemo(() => {
        const js = filterFlights({ flights: jumpSeaters, filters, timeMode });
        return js;
    }, [filters, timeMode, jumpSeaters]);
    
    useEffect(() => {  
        const results = {
            jumpseatersByDate: countFlightsByField(filteredJumpSeaters, timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase', 'barline', 'Seaters', 'SeatersAvg'),
            jumpseatersByDep: countFlightsByField(filteredJumpSeaters, 'dep', 'barline', 'Seaters', 'SeatersAvg'),
            jumpseatersBySector: countFlightsByField(filteredJumpSeaters, 'sector', 'barline', 'Seaters', 'SeatersAvg'),
            jumpseatersByFlightReg: countFlightsByField(filteredJumpSeaters, 'flightReg', 'barline', 'Seaters', 'SeatersAvg'),
            jumpseatersByACType: countFlightsByField(filteredJumpSeaters, 'acType', 'barline', 'Seaters', 'SeatersAvg'),
        };
        
        setData(results); 
    }, [timeMode, filteredJumpSeaters]); 
        
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(jumpSeaters)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(jumpSeaters.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [jumpSeaters]);
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Jump Seaters ${period}`;

    const chartMap = {
        jumpseatersByDate: [
            { component: <SortBarMultiLinesChart data={data.jumpseatersByDate} title={defaultTitle} bottomLegend='Date' leftLegend='No. Of Seaters' digit={0} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        jumpseatersByDep: [
            { component: <SortBarMultiLinesChart data={data.jumpseatersByDep} title={defaultTitle} bottomLegend='Port' leftLegend='No. Of Seaters' digit={0} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        jumpseatersBySector: [
            { component: <SortBarMultiLinesChart data={data.jumpseatersBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='No. Of Seaters' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        jumpseatersByFlightReg: [
            { component: <SortBarMultiLinesChart data={data.jumpseatersByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='No. Of Seaters' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
        jumpseatersByACType: [
            { component: <SortBarMultiLinesChart data={data.jumpseatersByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='No. Of Seaters' digit={0} barTranslateXOffSet={80} lineTranslateXOffSet={-30} />, style: {} },
        ],
    }; 
    
    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header 
                    title="Non Crew List" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '8rem', sm: '9rem' } }}
                        >
                            <InputLabel id="jumpseaters-view-label">View</InputLabel>
                            <Select
                                labelId="jumpseaters-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    {view in chartMap &&
                        renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
    
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}  

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default NonCrewListChart;
